import moment from "moment";

import { DIRECTION_NAMES } from "./constants";
import * as Angle_Helpers from "./wheelAngle";

export default class WheelSvg {
  constructor(centerX, centerY) {
    this.centerX = centerX;
    this.centerY = centerY;
  }

  createCircle(
    radius,
    strokeColor,
    fill = false,
    className = "gann-wheel-circle"
  ) {
    const circle = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "circle"
    );
    circle.setAttribute("cx", this.centerX);
    circle.setAttribute("cy", this.centerY);
    circle.setAttribute("r", radius);
    circle.setAttribute("stroke", strokeColor ? strokeColor : "#000");
    circle.setAttribute("fill", "transparent");

    if (fill) {
      circle.setAttribute("fill", "#000");
    }
    circle.classList.add(className);

    return circle;
  }

  createLine(startRadius, endRadius, angle, color) {
    const startAngle = 0;
    const endAngle = Angle_Helpers.getHalfCircleEndAngle(startAngle);

    const minRadianAngle = (startAngle * Math.PI) / 180;
    const maxRadianAngle = (endAngle * Math.PI) / 180;

    const minMarkX = this.centerX + startRadius * Math.cos(minRadianAngle);
    const maxMarkX = this.centerX + endRadius * Math.cos(maxRadianAngle);
    const minMarkY = this.centerY + startRadius * Math.sin(minRadianAngle);
    const maxMarkY = this.centerY + endRadius * Math.sin(maxRadianAngle);

    const line = document.createElementNS("http://www.w3.org/2000/svg", "line");

    line.setAttribute("x1", minMarkX);
    line.setAttribute("y1", minMarkY);
    line.setAttribute("x2", maxMarkX);
    line.setAttribute("y2", maxMarkY);
    line.setAttribute("stroke", `${color}`);
    line.setAttribute("stroke-width", "5");
    line.setAttribute("transform-origin", "50% 50%");
    line.setAttribute("transform", `rotate(${angle})`);

    return line;
  }

  createImages(radius, angle, href, size = 32) {
    const radianAngle = (angle * Math.PI) / 180;

    const markX = this.centerX + (radius - 35) * Math.cos(radianAngle);
    const markY = this.centerY + (radius - 35) * Math.sin(radianAngle);

    const image = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "image"
    );

    image.setAttribute("x", markX);
    image.setAttribute("y", markY);
    image.setAttribute("height", `${size}px`);
    image.setAttribute("width", `${size}px`);
    image.setAttribute("href", href);
    image.setAttribute("text-anchor", "start");
    image.setAttribute("alignment-baseline", "middle");

    // Calculate rotation angle to tilt text towards center
    const dx = markX - this.centerX;
    const dy = markY - this.centerY;
    const rotationAngle = Math.atan2(dy, dx) * (180 / Math.PI);

    // Apply rotation transform
    image.setAttribute(
      "transform",
      `rotate(${rotationAngle}, ${markX}, ${markY})`
    );

    return image;
  }

  createPriceImages(radius, angle, href, size = 24) {
    const radianAngle = (angle * Math.PI) / 180;

    const markX = this.centerX + (radius - 15) * Math.cos(radianAngle);
    const markY = this.centerY + (radius - 15) * Math.sin(radianAngle);

    const image = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "image"
    );

    image.setAttribute("x", markX);
    image.setAttribute("y", markY);
    image.setAttribute("height", `${size}px`);
    image.setAttribute("width", `${size}px`);
    image.setAttribute("href", href);

    image.setAttribute("transform", `translate(-15 -15)`);
    image.setAttribute("transform-origin", `${markX} ${markY}`);

    return image;
  }

  createTimeLabelText(radius, angle, textContent) {
    const textOffset = 0; // Distance between the line and the number

    const radianAngle = (angle * Math.PI) / 180;
    const markX = this.centerX + (radius - 15) * Math.cos(radianAngle);
    const markY = this.centerY + (radius - 15) * Math.sin(radianAngle);

    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.textContent = textContent.toString();
    // text.setAttribute("transform", `rotate(${angle} ${markX} ${markY})`);
    const xaxis = markX + textOffset * Math.cos(radianAngle);
    const yaxis = markY + textOffset * Math.sin(radianAngle);
    text.setAttribute("x", xaxis);
    text.setAttribute("y", yaxis);
    text.setAttribute("text-anchor", "middle");
    // text.setAttribute("alignment-baseline", "middle");

    // Calculate rotation angle to tilt text towards center
    const dx = xaxis - this.centerX;
    const dy = yaxis - this.centerY;
    const rotationAngle = Math.atan2(dy, dx) * (180 / Math.PI);

    // Apply rotation transform
    text.setAttribute(
      "transform",
      `rotate(${rotationAngle + 270}, ${xaxis}, ${yaxis})`
    );

    return text;
  }

  createDegreeMarkings(radius) {
    const tickLength = 8; // Length of the line before the number

    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("class", "degree-tick");

    for (let angle = 0; angle < 360; angle++) {
      // Change 5 to desired interval
      const radianAngle = (angle * Math.PI) / 180;
      const markX = this.centerX + radius * Math.cos(radianAngle);
      const markY = this.centerY + radius * Math.sin(radianAngle);

      // Line before the number
      const line = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "line"
      );
      let startX = this.centerX + (radius - tickLength) * Math.cos(radianAngle);
      let startY = this.centerY + (radius - tickLength) * Math.sin(radianAngle);
      if (angle % 10 === 0) {
        startX =
          this.centerX + (radius - (tickLength + 10)) * Math.cos(radianAngle);
        startY =
          this.centerY + (radius - (tickLength + 10)) * Math.sin(radianAngle);
      } else if (angle % 5 === 0) {
        startX =
          this.centerX + (radius - (tickLength + 6)) * Math.cos(radianAngle);
        startY =
          this.centerY + (radius - (tickLength + 6)) * Math.sin(radianAngle);
      }
      line.setAttribute("x1", startX);
      line.setAttribute("y1", startY);
      line.setAttribute("x2", markX);
      line.setAttribute("y2", markY);
      line.setAttribute("stroke", "#000");

      group.appendChild(line);
    }

    return group;
  }

  createDegreeText(radius, direction) {
    const textOffset = 0; // Distance between the line and the number

    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("class", "degree-text");

    for (let angle = 0; angle < 360; angle += 10) {
      // Change 5 to desired interval
      const radianAngle = (angle * Math.PI) / 180;
      const markX = this.centerX + radius * Math.cos(radianAngle);
      const markY = this.centerY + radius * Math.sin(radianAngle);

      const text = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      text.textContent = direction === "clockwise" ? angle : 360 - angle;
      if (text.textContent == 360) {
        text.textContent = 0;
      }
      // text.setAttribute("transform", `rotate(${angle} ${markX} ${markY})`);
      const xaxis = markX - textOffset * Math.cos(radianAngle);
      const yaxis = markY - textOffset * Math.sin(radianAngle);
      text.setAttribute("x", xaxis);
      text.setAttribute("y", yaxis);
      text.setAttribute("font-size", "10px");
      // text.setAttribute("text-anchor", "end");
      text.setAttribute("font-family", "Arial");

      // Calculate rotation angle to tilt text towards center
      const dx = xaxis - this.centerX;
      const dy = yaxis - this.centerY;
      const rotationAngle = Math.atan2(dy, dx) * (180 / Math.PI);

      // Apply rotation transform
      // text.setAttribute(
      //   "transform",
      //   `rotate(${rotationAngle}, ${xaxis}, ${yaxis})`
      // );
      text.setAttribute("transform", "translate(-5 0)");
      // text.setAttribute("transform", `rotate(0)`);
      text.setAttribute("transform-origin", `50% 50%`);
      // text.setAttribute("transform-origin", `${xaxis} ${yaxis})`);

      group.appendChild(text);
    }

    group.setAttribute("transform", "rotate(-0.3)");
    group.setAttribute("transform-origin", "50% 50%");

    return group;
  }

  createDateMarking(radius, direction, startAt) {
    const currentDate =
      direction === "clockwise" ? startAt : moment(startAt).add(358, "days");

    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("class", "date-text");

    for (let angle = 0; angle < 360; angle += 2) {
      let textOffset = 32;

      // Change 5 to desired interval
      const radianAngle = (angle * Math.PI) / 180;
      const markX = this.centerX + radius * Math.cos(radianAngle);
      const markY = this.centerY + radius * Math.sin(radianAngle);

      const text = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      text.textContent = currentDate.format("MMM DD");

      text.setAttribute("font-size", "10px");
      if (angle % 30 === 0) {
        text.setAttribute("font-size", "16px");
        textOffset += 15;
      }

      const xaxis = markX - textOffset * Math.cos(radianAngle);
      const yaxis = markY - textOffset * Math.sin(radianAngle);

      text.setAttribute("x", xaxis);
      text.setAttribute("y", yaxis);
      text.setAttribute("font-family", "serif");
      text.setAttribute("text-anchor", "start");
      text.setAttribute("alignment-baseline", "middle");

      // Calculate rotation angle to tilt text towards center
      const dx = xaxis - this.centerX;
      const dy = yaxis - this.centerY;
      const rotationAngle = Math.atan2(dy, dx) * (180 / Math.PI);

      // Apply rotation transform
      text.setAttribute(
        "transform",
        `rotate(${rotationAngle}, ${xaxis}, ${yaxis})`
      );

      group.appendChild(text);

      direction === "clockwise"
        ? currentDate.add(2, "day")
        : currentDate.subtract(2, "day");
    }

    return group;
  }

  createTimeMarking(radius, direction) {
    const textOffset = 20;
    const currentDate = moment().hours(0).minutes(0);

    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("class", "days-text");

    for (let angle = 0; angle < 360; angle += 1) {
      // Change 5 to desired interval
      const radianAngle = (angle * Math.PI) / 180;
      const markX = this.centerX + radius * Math.cos(radianAngle);
      const markY = this.centerY + radius * Math.sin(radianAngle);

      const text = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      text.textContent = currentDate.format("HH:mm");
      const xaxis = markX - textOffset * Math.cos(radianAngle);
      const yaxis = markY - textOffset * Math.sin(radianAngle);

      text.setAttribute("x", xaxis);
      text.setAttribute("y", yaxis);
      text.setAttribute("font-size", "8px");
      text.setAttribute("font-family", "Arial");
      text.setAttribute("text-anchor", "start");
      text.setAttribute("alignment-baseline", "middle");

      // Calculate rotation angle to tilt text towards center
      const dx = xaxis - this.centerX;
      const dy = yaxis - this.centerY;
      const rotationAngle = Math.atan2(dy, dx) * (180 / Math.PI);

      // Apply rotation transform
      text.setAttribute(
        "transform",
        `rotate(${rotationAngle}, ${xaxis}, ${yaxis})`
      );

      group.appendChild(text);

      direction === "clockwise"
        ? currentDate.subtract(4, "minute")
        : currentDate.add(4, "minute");
    }

    return group;
  }

  curvePath(angle, angleStep, outerRadius, innerRadius) {
    // Change 5 to desired interval
    const startRadians = (angle * Math.PI) / 180;
    const endRadians = ((angle + angleStep) * Math.PI) / 180;

    // Calculate points for the arc to represent the block
    const startX = this.centerX + outerRadius * Math.cos(startRadians);
    const startY = this.centerY + outerRadius * Math.sin(startRadians);
    const endX = this.centerX + outerRadius * Math.cos(endRadians);
    const endY = this.centerY + outerRadius * Math.sin(endRadians);
    const nextStartX = this.centerX + innerRadius * Math.cos(startRadians);
    const nextStartY = this.centerY + innerRadius * Math.sin(startRadians);
    const nextEndX = this.centerX + innerRadius * Math.cos(endRadians);
    const nextEndY = this.centerY + innerRadius * Math.sin(endRadians);

    // Create path element
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute(
      "d",
      `M ${startX},${startY} A ${outerRadius},${outerRadius} 0 0,1 ${endX},${endY} L ${nextEndX},${nextEndY} A ${innerRadius},${innerRadius} 0 0,0 ${nextStartX},${nextStartY} Z`
    );

    return path;
  }

  createAstroMarking(outerRadius, innerRadius, colorCode) {
    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("class", "astro-area-marking");

    let count = 1;
    const angleStep = 30;
    for (let angle = 0; angle < 360; angle += angleStep) {
      const path = this.curvePath(angle, angleStep, outerRadius, innerRadius);

      // Alternate fill colors
      if (count % 2 === 0) {
        path.setAttribute("fill", colorCode);
      } else {
        path.setAttribute("fill", "none");
      }

      group.appendChild(path);

      count += 1;
    }

    return group;
  }

  createDirections(radius) {
    const group = document.createElementNS("http://www.w3.org/2000/svg", "g");
    group.setAttribute("id", "directions");
    const textOffsetX = -20;
    const textOffsetY = -10;
    for (let angle = 0; angle < 360; angle += 90) {
      // Change 5 to desired interval
      const radianAngle = (angle * Math.PI) / 180;
      const markX = this.centerX + radius * Math.cos(radianAngle);
      const markY = this.centerY + radius * Math.sin(radianAngle);

      const text = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "text"
      );
      text.textContent = DIRECTION_NAMES[angle === 0 ? angle : 360 - angle];

      const xaxis = markX - textOffsetX * Math.cos(radianAngle);
      const yaxis = markY - textOffsetY * Math.sin(radianAngle);

      text.setAttribute("x", xaxis);
      text.setAttribute("y", yaxis);
      text.setAttribute("font-size", "30px");
      text.setAttribute("font-family", "ui-monospace");
      text.setAttribute("font-weight", "600");
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("alignment-baseline", "middle");

      group.appendChild(text);
    }

    return group;
  }

  createOverlayBar(
    deltaX,
    deltaY,
    radius,
    space,
    _barWidth,
    _barAngle,
    onSuccess
  ) {
    const startAngle = 0;
    const endAngle = Angle_Helpers.getHalfCircleEndAngle(startAngle);

    const minRadianAngle = (startAngle * Math.PI) / 180;
    const maxRadianAngle = (endAngle * Math.PI) / 180;

    const minMarkX = this.centerX + radius * Math.cos(minRadianAngle);
    const minMarkY = this.centerY + radius * Math.sin(minRadianAngle);

    const minMarkStartX =
      this.centerX + (radius - space * 4) * Math.cos(minRadianAngle);
    const minMarkStartY =
      this.centerY + (radius - space * 4) * Math.sin(minRadianAngle);

    const maxMarkX = this.centerX + radius * Math.cos(maxRadianAngle);
    const maxMarkY = this.centerY + radius * Math.sin(maxRadianAngle);

    const maxMarkStartX =
      this.centerX + (radius - space * 4) * Math.cos(maxRadianAngle);
    const maxMarkStartY =
      this.centerY + (radius - space * 4) * Math.sin(maxRadianAngle);

    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");

    const barWidth = Number(_barWidth) / 40;

    const start1RadianAngle =
      (Angle_Helpers.getAngleUpdate(startAngle, barWidth) * Math.PI) / 180;
    const startMarkY = this.centerY + radius * Math.sin(start1RadianAngle);
    const start1 = `M ${minMarkX},${startMarkY}`;

    const line1RadianAngle =
      (Angle_Helpers.getAngleUpdate(startAngle, barWidth, "minus") * Math.PI) /
      180;
    const line1MarkY = this.centerY + radius * Math.sin(line1RadianAngle);
    const line1 = `L ${minMarkX},${line1MarkY}`;

    const line2RadianAngle =
      (Angle_Helpers.getAngleUpdate(endAngle, barWidth) * Math.PI) / 180;
    const line2Y = this.centerY + radius * Math.sin(line2RadianAngle);
    const line2 = `L ${maxMarkX},${line2Y}`;

    const line3RadianAngle =
      (Angle_Helpers.getAngleUpdate(endAngle, barWidth, "minus") * Math.PI) /
      180;
    const line3Y = this.centerY + radius * Math.sin(line3RadianAngle);
    const line3 = `L ${maxMarkX},${line3Y}`;

    const start5 = `M ${minMarkX},${minMarkY}`;
    const line5 = `L ${minMarkStartX},${minMarkStartY}`;

    const start6 = `M ${maxMarkX},${maxMarkY}`;
    const line6 = `L ${maxMarkStartX},${maxMarkStartY}`;

    path.setAttribute(
      "d",
      `${start1} ${line1} ${line2} ${line3} Z ${start5} ${line5} ${start6} ${line6}`
    );
    // path.setAttribute("stroke-width", "0.8");
    path.setAttribute("stroke", "#000");
    path.setAttribute("fill", "#3d3c3526");
    path.onmousedown = Angle_Helpers.handleOverlayDown(
      { centerX: this.centerX, centerY: this.centerY, deltaX, deltaY },
      _barAngle,
      onSuccess
    );
    path.setAttribute("transform-origin", "50% 50%");
    path.setAttribute("transform", `rotate(${Number(_barAngle)})`);
    path.setAttribute("id", "overlay-bar");

    return path;
  }

  createOverlay(startRadius, angle, overlay) {
    const rotateAngle = angle + 7.5;

    const line = this.createLine(startRadius, -55, rotateAngle, overlay.color);
    line.setAttribute("stroke-opacity", 0.5);
    line.setAttribute("stroke-width", "18");

    return line;
  }
}
